import Vue from 'vue';
import {
  SUPPORT_DECISION,
  GPT_SUGGESTION_SOURCE,
  GPT_SUPPORT_ID,
} from '../../lib/const';
import gptApi from '@/api/gpt';

const state = {
  // active, autoReplyActive, offHoursAutoReplyActive, delay and aiCaseDelay - backend settings for suggestion generation
  active: false,
  autoReplyActive: false,
  offHoursAutoReplyActive: false,
  delay: 70,
  aiCaseDelay: 5,

  mode: null,
  loading: false,
  data: null,

  latestSuggestions: {},

  // AI Case killswitch - backend feature flag with its own admin endpoints
  aiCaseEnabled: false,
  aiCaseLoading: false,
};

const getters = {
  mode(state) {
    return state.mode;
  },
  latestSuggestions(state) {
    return state.latestSuggestions;
  },
};

const mutations = {
  SET_ACTIVE(state, { active }) {
    state.active = active;
  },

  SET_AUTO_REPLY_ACTIVE(state, { autoReplyActive }) {
    state.autoReplyActive = autoReplyActive;
  },

  SET_OFF_HOURS_AUTO_REPLY_ACTIVE(state, { offHoursAutoReplyActive }) {
    state.offHoursAutoReplyActive = offHoursAutoReplyActive;
  },

  SET_DELAY(state, { delay }) {
    state.delay = delay;
  },

  SET_AI_CASE_DELAY(state, { aiCaseDelay }) {
    state.aiCaseDelay = aiCaseDelay;
  },

  SET_MODE(state, chatId) {
    state.mode = chatId;
  },

  SET_LOADING(state, value) {
    state.loading = value;
  },

  SET_DATA(state, data) {
    if (data === null) {
      state.data = null;
      return;
    }

    // ignore state update if commited via socket for a different not currently active chatId
    if (data.chatId && data.chatId !== state.mode) return;

    const {
      assistantScore,
      messageIds,
      suggestions,
      suggestionSource,
      botSource,
      supportDecision = SUPPORT_DECISION.YES,
    } = data;
    state.data = {
      assistantScore,
      messageIds,
      suggestions,
      supportDecision,
      suggestionSource,
      botSource,
    };
  },

  UPDATE_DATA(state, { key, value }) {
    if (!state.data?.[key]) return;

    state.data[key] = value;
  },

  SET_LATEST_SUGGESTIONS(state, data) {
    state.latestSuggestions = data;
  },

  UPDATE_LATEST_SUGGESTIONS(state, { chatId, messageIds }) {
    const { latestSuggestions } = state;
    const updateValue = messageIds[0] || null;
    Vue.set(latestSuggestions, chatId, updateValue);
  },

  DELETE_LATEST_SUGGESTION(state, { chatId }) {
    const { latestSuggestions } = state;
    if (latestSuggestions[chatId]) {
      Vue.set(latestSuggestions, chatId, null);
    }
  },

  SET_AI_CASE_ENABLED(state, enabled) {
    state.aiCaseEnabled = enabled;
  },

  SET_AI_CASE_LOADING(state, value) {
    state.aiCaseLoading = value;
  },
};

const actions = {
  async getActive({ commit }) {
    try {
      const {
        data: { active },
      } = await gptApi.getChatbotSuggestionsActive();
      commit('SET_ACTIVE', { active });
    } catch (error) {
      console.error(error);
    }
  },
  async toggleActive({ state, commit }) {
    try {
      const newActive = !state.active;
      await gptApi.setChatbotSuggestionsActive({ active: newActive });
      commit('SET_ACTIVE', { active: newActive });
    } catch (error) {
      console.log(error);
    }
  },
  async getAutoReplyActive({ commit }) {
    try {
      const {
        data: { autoReplyActive },
      } = await gptApi.getChatbotSuggestionsAutoReplyActive();
      commit('SET_AUTO_REPLY_ACTIVE', { autoReplyActive });
    } catch (error) {
      console.error(error);
    }
  },
  async toggleAutoReplyActive({ state, commit }) {
    try {
      const newAutoReplyActive = !state.autoReplyActive;
      await gptApi.setChatbotSuggestionsAutoReplyActive({
        autoReplyActive: newAutoReplyActive,
      });
      commit('SET_AUTO_REPLY_ACTIVE', { autoReplyActive: newAutoReplyActive });
    } catch (error) {
      console.log(error);
    }
  },
  async getOffHoursAutoReplyActive({ commit }) {
    try {
      const {
        data: { offHoursAutoReplyActive },
      } = await gptApi.getChatbotSuggestionsOffHoursAutoReplyActive();
      commit('SET_OFF_HOURS_AUTO_REPLY_ACTIVE', { offHoursAutoReplyActive });
    } catch (error) {
      console.error(error);
    }
  },
  async toggleOffHoursAutoReplyActive({ state, commit }) {
    try {
      const newOffHoursAutoReplyActive = !state.offHoursAutoReplyActive;
      await gptApi.setChatbotSuggestionsOffHoursAutoReplyActive({
        offHoursAutoReplyActive: newOffHoursAutoReplyActive,
      });
      commit('SET_OFF_HOURS_AUTO_REPLY_ACTIVE', {
        offHoursAutoReplyActive: newOffHoursAutoReplyActive,
      });
    } catch (error) {
      console.log(error);
    }
  },
  async getDelay({ commit }) {
    try {
      const {
        data: { delay },
      } = await gptApi.getChatbotSuggestionsDelay();
      commit('SET_DELAY', { delay });
    } catch (error) {
      console.error(error);
    }
  },
  async setDelay({ commit }, { delay }) {
    try {
      await gptApi.setChatbotSuggestionsDelay({ delay });
      commit('SET_DELAY', { delay });
    } catch (error) {
      console.log(error);
    }
  },
  async getAiCaseDelay({ commit }) {
    try {
      const {
        data: { aiCaseDelay },
      } = await gptApi.getChatbotSuggestionsAiCaseDelay();
      commit('SET_AI_CASE_DELAY', { aiCaseDelay });
    } catch (error) {
      console.error(error);
    }
  },
  async setAiCaseDelay({ commit }, { aiCaseDelay }) {
    try {
      await gptApi.setChatbotSuggestionsAiCaseDelay({ aiCaseDelay });
      commit('SET_AI_CASE_DELAY', { aiCaseDelay });
    } catch (error) {
      console.log(error);
    }
  },

  async getGptResponse({ commit, rootGetters }, { chatId, messageIds }) {
    commit('SET_LOADING', true);
    const currentSupportName = rootGetters['support/currentSupport']?.firstName;

    try {
      const {
        data: { result },
      } = await gptApi.getGptResponse({
        chatId,
        messageIds,
        supportName: currentSupportName,
      });

      commit('SET_MODE', chatId);
      commit('SET_DATA', {
        messageIds,
        suggestions: [
          { message: result, multimedia: [], category: null, comment: null },
        ],
        suggestionSource: GPT_SUGGESTION_SOURCE.GPT_COMPLETION,
      });
      commit('chat/SET_MESSAGE_INPUT', result, { root: true });
    } catch (error) {
      console.error(error);
      commit('SET_MODE', null);
      commit('SET_DATA', null);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async tryShowGptSuggestion(
    { state, rootState, rootGetters, commit },
    { chatId }
  ) {
    commit('SET_LOADING', true);
    try {
      const chat = rootState.chat.byId[chatId];
      if (!chat?.lastCustomerMessageId) return;

      if (
        chatId === state.mode &&
        state.data?.messageIds[0] === chat.lastCustomerMessageId
      ) {
        const suggestionForInput = state.data.suggestions?.[0]?.message || '';
        commit('UPDATE_DATA', {
          key: 'supportDecision',
          value: SUPPORT_DECISION.YES,
        });
        commit('chat/SET_MESSAGE_INPUT', suggestionForInput, {
          root: true,
        });
        return;
      }

      const lastChatMessage = rootGetters['chat/lastChatMessage'](chatId);
      if (
        lastChatMessage?.supportId &&
        lastChatMessage.supportId !== GPT_SUPPORT_ID &&
        lastChatMessage?.messageId > 0
      )
        return;

      const { data: preGeneratedGptSuggestion } =
        await gptApi.getPreGeneratedGptSuggestion({
          chatId,
          messageId: chat.lastCustomerMessageId,
        });
      if (preGeneratedGptSuggestion) {
        const { assistantScore, suggestions, botSource } =
          preGeneratedGptSuggestion;
        commit('SET_MODE', chatId);
        commit('SET_DATA', {
          assistantScore,
          messageIds: [chat.lastCustomerMessageId],
          suggestions,
          suggestionSource: GPT_SUGGESTION_SOURCE.GPT_AUTO_COMPLETION,
          botSource,
        });
        const suggestionForInput = suggestions?.[0]?.message || '';
        commit('chat/SET_MESSAGE_INPUT', suggestionForInput, {
          root: true,
        });

        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getLatestSuggestions({ commit }) {
    try {
      const { data } = await gptApi.getLatestChatbotSuggestions();
      commit('SET_LATEST_SUGGESTIONS', data);
    } catch (error) {
      console.error(error);
    }
  },

  async getAiCaseConfig({ commit }) {
    commit('SET_AI_CASE_LOADING', true);
    try {
      const {
        data: { enabled },
      } = await gptApi.getAiCaseConfig();
      commit('SET_AI_CASE_ENABLED', enabled);
    } catch (error) {
      console.error(error);
    } finally {
      commit('SET_AI_CASE_LOADING', false);
    }
  },

  async setAiCaseConfig({ commit, state }, value) {
    const previous = state.aiCaseEnabled;
    commit('SET_AI_CASE_LOADING', true);
    try {
      const {
        data: { enabled },
      } = await gptApi.setAiCaseConfig(value);
      // treat the response as the source of truth
      commit('SET_AI_CASE_ENABLED', enabled);
    } catch (error) {
      console.error(error);
      // 500 means the change did not persist - keep the previous state
      commit('SET_AI_CASE_ENABLED', previous);
    } finally {
      commit('SET_AI_CASE_LOADING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
